import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import {
  adminGetAgentSettings,
  adminUpdateAgentSettings,
  adminRunAgentOnce,
} from "@/lib/blog-agent.functions";
import { BlogSubNav } from "@/components/admin/BlogSubNav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Loader2, Play, Sparkles } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/admin/blog-agent")({
  component: BlogAgentPage,
  head: () => ({ meta: [{ title: "AI blog agent · Admin" }, { name: "robots", content: "noindex" }] }),
});

function BlogAgentPage() {
  const getSettings = useServerFn(adminGetAgentSettings);
  const updateSettings = useServerFn(adminUpdateAgentSettings);
  const runOnce = useServerFn(adminRunAgentOnce);
  const qc = useQueryClient();
  const navigate = useNavigate();

  const q = useQuery({ queryKey: ["admin-agent-settings"], queryFn: () => getSettings() });
  const s = q.data as any;

  const [enabled, setEnabled] = useState(false);
  const [ppw, setPpw] = useState(3);
  const [autoHero, setAutoHero] = useState(true);
  const [autoPublish, setAutoPublish] = useState(false);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    if (!s) return;
    setEnabled(!!s.enabled);
    setPpw(s.posts_per_week ?? 3);
    setAutoHero(s.auto_hero_image !== false);
    setAutoPublish(!!s.auto_publish);
  }, [s]);

  const save = async () => {
    setSaving(true);
    try {
      await updateSettings({
        data: {
          enabled,
          posts_per_week: ppw,
          auto_hero_image: autoHero,
          auto_publish: autoPublish,
        },
      });
      await qc.invalidateQueries({ queryKey: ["admin-agent-settings"] });
      toast.success("Settings saved");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Save failed");
    } finally { setSaving(false); }
  };

  const run = async (force: boolean) => {
    setRunning(true);
    try {
      const r = (await runOnce({ data: { force } })) as any;
      if (!r.ran) {
        toast.message(r.reason ?? "Agent didn't run");
      } else {
        toast.success("Draft created");
        if (r.post?.id) void navigate({ to: "/admin/posts/$id", params: { id: r.post.id } });
      }
      await qc.invalidateQueries({ queryKey: ["admin-agent-settings"] });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Run failed");
    } finally { setRunning(false); }
  };

  return (
    <div>
      <BlogSubNav />
      <div className="grid gap-6 lg:grid-cols-[1fr_360px]">
        <div>
          <h1 className="font-display text-2xl font-bold">AI blog agent</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Automates SEO-optimized reverse-mortgage drafts using Lovable AI. Drafts require human review before publishing.
          </p>

          <div className="mt-6 space-y-4 rounded-2xl border border-border bg-card p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-semibold">Agent enabled</p>
                <p className="text-xs text-muted-foreground">When on, scheduled runs create drafts up to your weekly pace.</p>
              </div>
              <Switch checked={enabled} onCheckedChange={setEnabled} />
            </div>
            <div>
              <Label htmlFor="ppw">Posts per week (1–21)</Label>
              <Input id="ppw" type="number" min={1} max={21} value={ppw} onChange={(e) => setPpw(Number(e.target.value) || 1)} />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-semibold">Auto-generate hero image</p>
                <p className="text-xs text-muted-foreground">Uses Gemini image generation for each draft.</p>
              </div>
              <Switch checked={autoHero} onCheckedChange={setAutoHero} />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-semibold">Auto-publish (not recommended)</p>
                <p className="text-xs text-muted-foreground">Reverse mortgages are regulated. Keep off for human review.</p>
              </div>
              <Switch checked={autoPublish} onCheckedChange={setAutoPublish} />
            </div>
            <div className="flex justify-end">
              <Button disabled={saving} onClick={save}>
                {saving ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
                Save settings
              </Button>
            </div>
          </div>
        </div>

        <aside className="space-y-6">
          <div className="rounded-2xl border border-border bg-card p-5">
            <p className="mb-3 text-xs font-semibold uppercase tracking-widest text-muted-foreground">Run agent</p>
            <div className="flex flex-col gap-2">
              <Button disabled={running} onClick={() => run(false)}>
                {running ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Play className="mr-1 h-4 w-4" />}
                Run once (respect pace)
              </Button>
              <Button variant="outline" disabled={running} onClick={() => run(true)}>
                <Sparkles className="mr-1 h-4 w-4" /> Force one draft now
              </Button>
            </div>
            {s?.last_run_at && (
              <p className="mt-3 text-xs text-muted-foreground">
                Last run: {new Date(s.last_run_at).toLocaleString()}
              </p>
            )}
          </div>

          <div className="rounded-2xl border border-border bg-card p-5 text-sm text-muted-foreground">
            <p className="font-semibold text-foreground">How it works</p>
            <ol className="mt-2 list-decimal space-y-1 pl-4">
              <li>Picks the highest-priority queued topic, or a smart SEO fallback if none exist.</li>
              <li>Drafts a 1600–2200 word article with FAQ + internal links to /dscr-loans, /apply, /dscr-calculator.</li>
              <li>Generates a hero image (if enabled) and stores a private URL.</li>
              <li>Saves as a <code>draft</code> for you to review, tweak, and publish.</li>
            </ol>
          </div>
        </aside>
      </div>
    </div>
  );
}
